import { db } from '../firebase';
import { collection, query, where, getDocs, or, and } from 'firebase/firestore';

/**
 * Check if two time ranges overlap
 * @param {string} start1 - Start time in HH:MM format
 * @param {string} end1 - End time in HH:MM format
 * @param {string} start2 - Start time in HH:MM format
 * @param {string} end2 - End time in HH:MM format
 * @returns {boolean} - True if times overlap
 */
export const doTimesOverlap = (start1, end1, start2, end2) => {
	const toMinutes = (timeStr) => {
		const [hours, minutes] = timeStr.split(':').map(Number);
		return hours * 60 + minutes;
	};

	const start1Mins = toMinutes(start1);
	const end1Mins = toMinutes(end1);
	const start2Mins = toMinutes(start2);
	const end2Mins = toMinutes(end2);

	// Check if ranges overlap
	return start1Mins < end2Mins && end1Mins > start2Mins;
};

/**
 * Check for scheduling conflicts with existing time slots
 * @param {Object} timeSlotData - The time slot to check
 * @returns {Promise<Array>} - Array of conflicting time slots
 */
export const checkTimeSlotConflicts = async (timeSlotData) => {
	const {
		dayOfWeek,
		specificDate,
		startTime,
		endTime,
		instructor,
		location,
		isRecurring,
		excludeSlotId = null // Optional: exclude a specific slot (for editing)
	} = timeSlotData;

	const conflicts = [];

	try {
		const timeSlotsRef = collection(db, 'timeSlots');
		let queryConstraints = [];

		// Query based on whether this is recurring or one-time
		if (isRecurring && dayOfWeek !== null) {
			// Check for recurring conflicts on the same day
			queryConstraints = [
				where('status', '==', 'assigned'),
				where('isRecurring', '==', true),
				where('dayOfWeek', '==', dayOfWeek)
			];
		} else if (!isRecurring && specificDate) {
			// Check for one-time conflicts on the same date
			queryConstraints = [
				where('status', '==', 'assigned'),
				where('specificDate', '==', specificDate)
			];
		} else {
			return conflicts; // Invalid data
		}

		const q = query(timeSlotsRef, ...queryConstraints);
		const snapshot = await getDocs(q);

		// Check each existing slot for time/instructor/location conflicts
		snapshot.docs.forEach(doc => {
			// Skip if this is the slot we're editing
			if (excludeSlotId && doc.id === excludeSlotId) {
				return;
			}

			const slot = doc.data();

			// Check if times overlap
			if (doTimesOverlap(startTime, endTime, slot.startTime, slot.endTime)) {
				// Check for instructor conflict (same instructor can't teach two lessons at once)
				if (instructor && slot.instructor === instructor) {
					conflicts.push({
						id: doc.id,
						type: 'instructor',
						reason: `Instructor ${instructor} already has a lesson at this time`,
						...slot
					});
				}

				// Check for location conflict (same room can't be used twice)
				if (location && slot.location === location) {
					conflicts.push({
						id: doc.id,
						type: 'location',
						reason: `Location ${location} is already booked at this time`,
						...slot
					});
				}
			}
		});

		// Additional check: if this is a recurring slot, also check one-time slots
		// on all future instances of this day
		if (isRecurring && dayOfWeek !== null) {
			const oneTimeQuery = query(
				timeSlotsRef,
				where('status', '==', 'assigned'),
				where('isRecurring', '==', false)
			);

			const oneTimeSnapshot = await getDocs(oneTimeQuery);

			oneTimeSnapshot.docs.forEach(doc => {
				const slot = doc.data();

				// Check if the one-time slot's date falls on our recurring day
				if (slot.specificDate) {
					const slotDate = new Date(slot.specificDate);
					if (slotDate.getDay() === dayOfWeek) {
						// Check time overlap
						if (doTimesOverlap(startTime, endTime, slot.startTime, slot.endTime)) {
							if (instructor && slot.instructor === instructor) {
								conflicts.push({
									id: doc.id,
									type: 'instructor',
									reason: `Instructor ${instructor} has a one-time lesson on ${slot.specificDate}`,
									...slot
								});
							}

							if (location && slot.location === location) {
								conflicts.push({
									id: doc.id,
									type: 'location',
									reason: `Location ${location} is booked for a one-time lesson on ${slot.specificDate}`,
									...slot
								});
							}
						}
					}
				}
			});
		}

	} catch (error) {
		console.error('Error checking time slot conflicts:', error);
		throw error;
	}

	// Remove duplicates (same slot might conflict on both instructor and location)
	const uniqueConflicts = conflicts.filter((conflict, index, self) =>
		index === self.findIndex(c => c.id === conflict.id)
	);

	return uniqueConflicts;
};

/**
 * Get all time slots for a specific student
 * @param {string} studentId - The student's ID
 * @returns {Promise<Array>} - Array of time slots
 */
export const getStudentTimeSlots = async (studentId) => {
	try {
		const timeSlotsRef = collection(db, 'timeSlots');
		const q = query(
			timeSlotsRef,
			where('studentId', '==', studentId),
			where('status', '==', 'assigned')
		);

		const snapshot = await getDocs(q);
		return snapshot.docs.map(doc => ({
			id: doc.id,
			...doc.data()
		}));
	} catch (error) {
		console.error('Error fetching student time slots:', error);
		throw error;
	}
};

/**
 * Get all time slots for a specific day and time range
 * @param {number} dayOfWeek - Day of week (0-6)
 * @param {string} date - Specific date (YYYY-MM-DD) for one-time slots
 * @returns {Promise<Array>} - Array of time slots
 */
export const getTimeSlotsForDay = async (dayOfWeek, date = null) => {
	try {
		const timeSlotsRef = collection(db, 'timeSlots');
		const slots = [];

		// Get recurring slots for this day
		if (dayOfWeek !== null) {
			const recurringQuery = query(
				timeSlotsRef,
				where('isRecurring', '==', true),
				where('dayOfWeek', '==', dayOfWeek),
				where('status', '==', 'assigned')
			);

			const recurringSnapshot = await getDocs(recurringQuery);
			slots.push(...recurringSnapshot.docs.map(doc => ({
				id: doc.id,
				...doc.data()
			})));
		}

		// Get one-time slots for this specific date
		if (date) {
			const oneTimeQuery = query(
				timeSlotsRef,
				where('isRecurring', '==', false),
				where('specificDate', '==', date),
				where('status', '==', 'assigned')
			);

			const oneTimeSnapshot = await getDocs(oneTimeQuery);
			slots.push(...oneTimeSnapshot.docs.map(doc => ({
				id: doc.id,
				...doc.data()
			})));
		}

		// Sort by start time
		return slots.sort((a, b) => {
			const timeA = a.startTime.split(':').map(Number);
			const timeB = b.startTime.split(':').map(Number);
			return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
		});
	} catch (error) {
		console.error('Error fetching time slots for day:', error);
		throw error;
	}
};

/**
 * Format time from 24-hour to 12-hour format
 * @param {string} time24 - Time in HH:MM format
 * @returns {string} - Time in 12-hour format
 */
export const formatTime12Hour = (time24) => {
	const [hours, minutes] = time24.split(':').map(Number);
	const period = hours >= 12 ? 'PM' : 'AM';
	const hours12 = hours % 12 || 12;
	return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
};

/**
 * Get the day name from day number
 * @param {number} dayNum - Day number (0-6)
 * @returns {string} - Day name
 */
export const getDayName = (dayNum) => {
	const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
	return days[dayNum] || '';
};

/**
 * Get all available (open/unassigned) time slots
 * @returns {Promise<Array>} - Array of open time slots
 */
export const getAvailableTimeSlots = async () => {
	try {
		const timeSlotsRef = collection(db, 'timeSlots');
		const q = query(
			timeSlotsRef,
			where('status', '==', 'open')
		);

		const snapshot = await getDocs(q);
		return snapshot.docs.map(doc => ({
			id: doc.id,
			...doc.data()
		}));
	} catch (error) {
		console.error('Error fetching available time slots:', error);
		throw error;
	}
};
